import React, {
  FC,
  MouseEvent,
  createRef,
  useCallback,
  useEffect,
  useState,
} from "react";
import {
  LoginButton,
  LoginButtonProps,
} from "../components/LoginButton/LoginButton";
import { SearchBar } from "../components/Header/SearchBar/SearchBar";
import { useKeycloak } from "@react-keycloak/ssr";
import { KeycloakInstance } from "keycloak-js";
import { usePortalAuth } from "../providers/keycloak-provider";
import { usePortalProvider } from "../providers/portal-provider";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import { useHistory, useLocation } from "react-router-dom";
import SearchIcon from "@mui/icons-material/Search";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import CloseIcon from "@mui/icons-material/Close";
import {
  HeaderActionsContent,
  HeaderActionsDialog,
  HeaderActionsIconButton,
  HeaderActionsWrap,
  MenuContainer,
  MenuItem,
} from "../components/Header/style";
import MenuIcon from "@mui/icons-material/Menu";
import { getUserProfile } from "shared-hooks";
import {
  MdOutlineHome,
  MdOutlineNotificationsNone,
  MdOutlinePerson,
  MdOutlineRoomService,
} from "react-icons/md";
import { sendPostMessageToChatIA } from "../util/send-post-message-chat-ia";
import { UserProfileDropdown } from "../components/UserProfileDropdown/UserProfileDropdown";
import { Box, Modal, SvgIconProps, SvgIcon } from "@mui/material";
import { IconType } from "react-icons";
import { VoiceButton } from "../components/VoiceButton/VoiceButton";
import { useVoiceNavigation } from "../hooks/use-voice-navigation";

interface SearchDialogProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  onClickSearch: (search: string) => void;
  onClickSearchSuggestion: (search: string) => void;
  isSmallScreen: boolean;
  inputRef: React.RefObject<HTMLInputElement>;
}

const SimpleDialog: FC<SearchDialogProps> = ({
  onClickSearch,
  onClickSearchSuggestion,
  open,
  setOpen,
  inputRef,
}) => {
  return (
    <HeaderActionsDialog
      open={open}
      onClose={() => setOpen(false)}
      TransitionProps={{
        onEntered: () => {
          if (inputRef.current) {
            inputRef.current.focus();
          }
        },
      }}
    >
      <DialogTitle>
        <HeaderActionsIconButton
          aria-label="close"
          onClick={() => setOpen(false)}
          disableRipple
          className="dialog"
        >
          <CloseIcon />
        </HeaderActionsIconButton>
      </DialogTitle>
      <DialogContent sx={{ marginTop: "20px" }}>
        <SearchBar
          className="xvia-home-search__input"
          placeholder={"Pesquisar..."}
          onClickSearch={onClickSearch}
          onClickSearchSuggestion={(suggestion) =>
            onClickSearchSuggestion(suggestion.slug)
          }
          aria-label="Campo de busca de serviços"
          ref={inputRef}
        />
      </DialogContent>
    </HeaderActionsDialog>
  );
};

export const HeaderActionsContainer: FC = () => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down(880));
  const isExtraSmallScreen = useMediaQuery(theme.breakpoints.down(425));
  const { keycloak, initialized } = useKeycloak<KeycloakInstance>();
  const { login, logout } = usePortalAuth();
  const [loading, setLoading] = useState<boolean>(false);
  const [open, setOpen] = useState(false);
  const [state, setState] = useState(false);
  const { ssr, profile, setProfile, sendMessageToApp } = usePortalProvider();
  const history = useHistory();
  const location = useLocation();

  const {
    status: voiceStatus,
    lastTranscript,
    lastError,
    isSupported: isVoiceSupported,
    start: startVoice,
    stop: stopVoice,
  } = useVoiceNavigation();

  const renderIcon = (icon: IconType, props?: SvgIconProps) => (
    <SvgIcon component={icon} {...props} />
  );

  const menuItems = [
    { label: "Início", path: "/" },
    { label: "Serviços", path: "/carta-de-servicos" },
    { label: "Notícias", path: "/noticias" },
    { label: "Governo", path: "/secretarias-e-orgaos" },
  ];

  const menuCitizenItems = [
    {
      label: "Inicio",
      path: "/",
      icon: renderIcon(MdOutlineHome),
    },
    {
      label: "Meus Dados",
      path: "/meus-dados",
      icon: renderIcon(MdOutlinePerson),
    },
    {
      label: "Agendamentos",
      path: "/appointments-attendances",
      icon: renderIcon(MdOutlineRoomService),
    },
    {
      label: "Notificações",
      path: "/notificacoes",
      icon: renderIcon(MdOutlineNotificationsNone),
    },
  ];

  const [currentPath, setCurrentPath] = useState<string>(
    location.pathname.includes("/app/catalog")
      ? location.pathname.replace("/app/catalog", "")
      : location.pathname.replace("/app", "")
  );

  const handleCatalogClick = (menuItemPath: string, mouseEvent: MouseEvent) => {
    if (location.pathname.includes("/app/catalog")) {
      mouseEvent.preventDefault();
      sendMessageToApp?.({
        detail: {
          pushRoute: menuItemPath,
        },
      });
      setCurrentPath(menuItemPath);
    } else if (window) {
      window.location.href = `/app/catalog${menuItemPath}`;
    }
    setState(false);
  };

  const handlePanelClick = (menuItemPath: string, mouseEvent: MouseEvent) => {
    if (location.pathname.includes("/app/painel-cidadao")) {
      mouseEvent.preventDefault();
      sendMessageToApp?.({
        detail: {
          pushRoute: menuItemPath,
        },
      });
    } else if (window) {
      window.location.href = `/app/painel-cidadao${menuItemPath}`;
    }
    setState(false);
  };

  useEffect(() => {
    const loadUserInfo = async () => {
      setLoading(true);
      if (!initialized) return;

      setLoading(false);

      if (keycloak?.authenticated) {
        setLoading(true);
        let profileUserInfo;

        try {
          const { userInfo, ...profile } = await getUserProfile(
            keycloak?.token
          );

          profileUserInfo = { ...userInfo, ...profile };

          setProfile(profileUserInfo);
        } catch (e) {
          const userInfoKeycloak = await keycloak?.loadUserInfo();
          profileUserInfo = userInfoKeycloak;

          setProfile(userInfoKeycloak);
        } finally {
          const postMessageType = "user_info";
          const postMessagePayload = JSON.stringify(profileUserInfo);

          sendPostMessageToChatIA(postMessageType, postMessagePayload);
          setLoading(false);
        }
      }
    };

    loadUserInfo();
  }, [keycloak, initialized, setProfile]);

  const onLogin = useCallback(() => {
    if (ssr) {
      login();
      return;
    }
    keycloak?.login();
  }, [login, keycloak, ssr]);

  const onLogout = useCallback(() => {
    const postMessageType = "logout";

    sendPostMessageToChatIA(postMessageType);

    if (ssr) {
      logout();
      return;
    }
    keycloak?.logout();
  }, [logout, keycloak, ssr]);

  const onAccountManagementRequest = () => {
    setState(false);
    history.push("/app/painel-cidadao");
  };

  const onClickSearch = useCallback(
    (value: string) => {
      setOpen(false);
      setState(false);

      if (
        sendMessageToApp &&
        (location.pathname.includes("/app/catalog") ||
          location.pathname === "" ||
          location.pathname === "/")
      ) {
        sendMessageToApp({
          detail: {
            pushRoute: `/search/${value}`,
          },
        });
      } else if (window) {
        window.location.href = `/app/catalog/search/${value}`;
      }
    },
    [location, sendMessageToApp]
  );

  const onClickSearchSuggestion = useCallback(
    (value: string) => {
      setOpen(false);
      setState(false);

      if (
        sendMessageToApp &&
        (location.pathname.includes("/app/catalog") ||
          location.pathname === "" ||
          location.pathname === "/")
      ) {
        sendMessageToApp({
          detail: {
            pushRoute: `/carta-de-servicos/${value}`,
          },
        });
      } else if (window) {
        window.location.href = `/app/catalog/carta-de-servicos/${value}`;
      }
    },
    [location, sendMessageToApp]
  );

  const props: LoginButtonProps = {
    onLogin,
    onLogout,
    onAccountManagementRequest,
    profile,
    loading,
  };

  const checkCurrentPath = useCallback(
    (itemPath: string) => {
      const pathParts = currentPath.split("/").filter(Boolean); // remove strings vazias
      const firstSegment = pathParts.length ? `/${pathParts[0]}` : "/";

      return firstSegment === itemPath;
    },
    [currentPath]
  );

  const inputRef = createRef<HTMLInputElement>();

  useEffect(() => {
    const interval = setInterval(() => {
      const newPath = window.location.pathname.includes("/app/catalog")
        ? window.location.pathname.replace("/app/catalog", "") || "/"
        : window.location.pathname;

      if (newPath !== currentPath) {
        setCurrentPath(newPath);
      }
    }, 300);

    return () => clearInterval(interval);
  }, [currentPath]);

  return (
    <HeaderActionsWrap
      isSmallScreen={isSmallScreen}
      isExtraSmallScreen={isExtraSmallScreen}
    >
      {!isSmallScreen && (
        <HeaderActionsContent>
          <MenuContainer isSmallScreen={isSmallScreen}>
            {menuItems.map((item) => (
              <MenuItem
                key={item.path}
                href={`/app/catalog${item.path}`}
                onClick={(e: MouseEvent) => handleCatalogClick(item.path, e)}
                className={checkCurrentPath(item.path) ? "active" : ""}
              >
                {item.label}
              </MenuItem>
            ))}
          </MenuContainer>
          <HeaderActionsIconButton
            onClick={() => setOpen(true)}
            aria-label="Pesquiser no portal"
            disableRipple
            className="search"
          >
            <SearchIcon />
          </HeaderActionsIconButton>
          <VoiceButton
            status={voiceStatus}
            lastTranscript={lastTranscript}
            lastError={lastError}
            isSupported={isVoiceSupported}
            onStart={startVoice}
            onStop={stopVoice}
          />
          <LoginButton {...props} />
        </HeaderActionsContent>
      )}
      <SimpleDialog
        open={open}
        setOpen={setOpen}
        onClickSearch={onClickSearch}
        onClickSearchSuggestion={onClickSearchSuggestion}
        isSmallScreen={isSmallScreen}
        inputRef={inputRef}
      />

      {isSmallScreen && (
        <>
          <VoiceButton
            status={voiceStatus}
            lastTranscript={lastTranscript}
            lastError={lastError}
            isSupported={isVoiceSupported}
            onStart={startVoice}
            onStop={stopVoice}
          />
          <LoginButton {...props} />
          <HeaderActionsIconButton
            onClick={() => setState(true)}
            aria-label="menu"
            className="menu"
          >
            <MenuIcon />
          </HeaderActionsIconButton>
          <Modal open={state} onClose={() => setState(false)}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                paddingTop: "40px",
              }}
            >
              <UserProfileDropdown
                user={profile as any}
                portalNavLinks={menuItems as any}
                onNavigateCatalog={handleCatalogClick}
                onNavigatePanel={handlePanelClick}
                onLogout={onLogout}
                loginProps={props}
                onClose={() => setState(false)}
                citizenNavLinks={menuCitizenItems as any}
              />
            </Box>
          </Modal>
        </>
      )}
    </HeaderActionsWrap>
  );
};
